import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import { format, isValid, parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import CalenderBase2 from "../Calender/CalendarBase2";
import { StarIcon } from "@heroicons/react/24/solid";
import { FieldError } from "react-hook-form";

interface DateInputProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  placeholder?: string;
  className?: string;
  error?: FieldError;
  isRequired?: boolean;
  containerClass?: string;
}

// turns raw keystrokes into "DD/MM/YYYY" as the user types
const maskDate = (raw: string) => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const parts: string[] = [];
  if (digits.length > 0) parts.push(digits.slice(0, 2));
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  return parts.join("/");
};

const clampDate = (date: Date, min?: Date, max?: Date) => {
  if (min && date < min) return min;
  if (max && date > max) return max;
  return date;
};

const DateInput = ({
  value,
  onChange,
  label,
  disabled,
  min,
  max,
  placeholder = "DD/MM/YYYY",
  className,
  error,
  isRequired = true,
  containerClass = "",
}: DateInputProps) => {
  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const parsed = parse(value, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : undefined;
  }, [value]);

  const minDate = useMemo(
    () => (min ? parse(min, "yyyy-MM-dd", new Date()) : undefined),
    [min]
  );
  const maxDate = useMemo(
    () => (max ? parse(max, "yyyy-MM-dd", new Date()) : undefined),
    [max]
  );

  const [month, setMonth] = useState<Date | undefined>(() =>
    clampDate(selectedDate ?? new Date(), minDate, maxDate)
  );

  useEffect(() => {
    setMonth((prev) => clampDate(prev ?? new Date(), minDate, maxDate));
  }, [minDate, maxDate]);

  const [text, setText] = useState(
    selectedDate ? format(selectedDate, "dd/MM/yyyy") : ""
  );

  useEffect(() => {
    setText(selectedDate ? format(selectedDate, "dd/MM/yyyy") : "");
    if (selectedDate) setMonth(selectedDate);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskDate(e.target.value);
    setText(masked);

    if (masked.length < 10) return;

    const parsed = parse(masked, "dd/MM/yyyy", new Date());
    if (!isValid(parsed)) {
      onChange("");
      return;
    }

    setMonth(clampDate(parsed, minDate, maxDate));
    onChange(format(parsed, "yyyy-MM-dd"));
  };

  const handleBlur = () => {
    if (text.length < 10) {
      setText(selectedDate ? format(selectedDate, "dd/MM/yyyy") : "");
    }
  };

  const handleCalendarSelect = (date: Date | undefined, close: () => void) => {
    if (!date) {
      onChange("");
      setText("");
    } else {
      onChange(format(date, "yyyy-MM-dd"));
      setText(format(date, "dd/MM/yyyy"));
      setMonth(date);
    }
    close();
  };

  return (
    <div className={`text-body font-semibold text-xs ${containerClass}`}>
      {label && (
        <label className="flex items-center gap-1 pl-1">
          {label}
          {isRequired && (
            <StarIcon className="size-2 text-error -translate-y-1" />
          )}
        </label>
      )}

      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={text}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`inputGroup h-10 w-full text-sm 
              text-body font-semibold pl-2 py-1 placeholder:font-medium placeholder:text-darkGray/80 outline-none rounded-lg
               z-10 border-2 border-extraLightGray focus:border-primary/50 disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-extraLightGray/30 
                ${className}`}
        />

        <Popover className="absolute right-2 top-2">
          {({ close }) => (
            <>
              <PopoverButton
                disabled={disabled}
                className="disabled:cursor-not-allowed data-[disabled]:opacity-50"
              >
                <CalendarIcon className="size-5 text-gray-500" />
              </PopoverButton>

              <Transition
                enter="transition duration-100"
                enterFrom="scale-95 opacity-0"
                enterTo="scale-100 opacity-100"
                leave="transition duration-100"
                leaveFrom="scale-100 opacity-100"
                leaveTo="scale-95 opacity-0"
              >
                <PopoverPanel
                  anchor="bottom end"
                  className="rounded-md border border-lightGray bg-white shadow-lg z-50 [--anchor-gap:8px] h-72 hide-scrollbar"
                >
                  <CalenderBase2
                    month={month}
                    onMonthChange={setMonth}
                    selected={selectedDate}
                    onSelect={(date) => handleCalendarSelect(date, close)}
                    fromDate={minDate}
                    toDate={maxDate}
                  />
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>

      {error && (
        <p className="text-xs text-error/90 font-medium pl-1">
          {error?.message}
        </p>
      )}
    </div>
  );
};

export default DateInput;
